// src/hooks/useUserClinicMapping.js
import { useEffect, useState } from "react";
import { doc, onSnapshot, getFirestore } from "firebase/firestore";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { firebaseApp } from "../firebase/firebaseApp";
import { claimLegacyClinicStudyId } from "../utils/firebase/claimLegacyClinicStudyId";

function clean(value) {
  if (value === undefined || value === null) return null;
  const next = String(value).trim();
  return next.length ? next : null;
}

function readSubjectIdFromProfile(profile) {
  const normalized = profile?.responses ?? profile ?? null;
  return clean(normalized?.id);
}

export function useUserClinicMapping({ uid }) {
  const db = getFirestore(firebaseApp);
  const { getCachedProfile, ensureProfile } = useProfilePrefetch();

  const [state, setState] = useState({
    loading: true,
    resolved: false,
    subjectId: null,
    clinicStudyId: null,
    error: null,
  });

  useEffect(() => {
    if (!uid) {
      setState({
        loading: false,
        resolved: false,
        subjectId: null,
        clinicStudyId: null,
        error: null,
      });
      return;
    }

    const ref = doc(db, "user_clinic_mapping", uid);
    let cancelled = false;
    let resolveCounter = 0;

    setState({
      loading: true,
      resolved: false,
      subjectId: null,
      clinicStudyId: null,
      error: null,
    });

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const resolutionId = resolveCounter + 1;
        resolveCounter = resolutionId;

        (async () => {
          const data = snap.exists() ? snap.data() || {} : {};
          const mappedSubjectId = clean(data.subjectId);
          const mappedClinicStudyId = clean(data.clinicStudyId);

          if (mappedClinicStudyId) {
            if (cancelled || resolutionId !== resolveCounter) return;

            setState({
              loading: false,
              resolved: true,
              subjectId: mappedSubjectId,
              clinicStudyId: mappedClinicStudyId,
              error: null,
            });
            return;
          }

          setState((prev) => ({
            ...prev,
            loading: true,
            resolved: false,
            subjectId: mappedSubjectId ?? prev.subjectId ?? null,
            clinicStudyId: null,
            error: null,
          }));

          let resolvedSubjectId = mappedSubjectId;

          if (!resolvedSubjectId) {
            const cached = getCachedProfile(uid);
            const cachedProfile =
              cached?.status === "ready" ? cached.data ?? null : null;

            resolvedSubjectId = readSubjectIdFromProfile(cachedProfile);

            if (!resolvedSubjectId) {
              const latestProfile = await ensureProfile({ uid });
              resolvedSubjectId = readSubjectIdFromProfile(latestProfile);
            }
          }

          if (cancelled || resolutionId !== resolveCounter) return;

          if (!resolvedSubjectId) {
            setState({
              loading: false,
              resolved: true,
              subjectId: mappedSubjectId,
              clinicStudyId: null,
              error: null,
            });
            return;
          }

          const claim = await claimLegacyClinicStudyId({
            uid,
            subjectId: resolvedSubjectId,
          });

          if (cancelled || resolutionId !== resolveCounter) return;

          setState({
            loading: false,
            resolved: true,
            subjectId: resolvedSubjectId,
            clinicStudyId: claim.ok ? clean(claim.clinicStudyId) : null,
            error: claim.ok
              ? null
              : claim.reason === "fetch_failed"
                ? "backfill_failed"
                : null,
          });
        })().catch(() => {
          if (cancelled || resolutionId !== resolveCounter) return;

          setState({
            loading: false,
            resolved: true,
            subjectId: null,
            clinicStudyId: null,
            error: "fetch_failed",
          });
        });
      },
      () => {
        setState({
          loading: false,
          resolved: true,
          subjectId: null,
          clinicStudyId: null,
          error: "fetch_failed",
        });
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uid, db, getCachedProfile, ensureProfile]);

  return state;
}
